import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { logout, getProfile } from "../../services/authService";
import api from "../../services/api";

export default function Navbar() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [userId, setUserId] = useState("");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getProfile();
        const user = res.data?.data?.user || res.data?.data || res.data;
        setUserName(user?.name || "");
        setUserRole(user?.role === 0 || user?.role === "0" ? "Primary Merchant" : user?.role || "Merchant");
        setUserId(user?.merchant_id || user?.user_id || user?.id || "");
      } catch { /* ignore */ }
    })();
    (async () => {
      try {
        const res = await api.get("/auth/profile-image");
        const path = res.data?.data?.image_url;
        if (path) {
          const base = (process.env.REACT_APP_API_BASE_URL || "https://partner.krestapay.co.in/api").replace(/\/api$/, "");
          setProfilePhoto(`${base}${path}`);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (_) {
      // ignore
    } finally {
      localStorage.removeItem("token");
      toast.success("Logged out successfully.");
      navigate("/");
    }
  };

  return (
    <div className="h-[52px] bg-white flex items-center justify-between px-6 shrink-0">

      {/* LEFT: Search */}
      <div className="flex items-center flex-1 max-w-[520px]">
        <div className="relative w-full">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2"
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="none"
          >
            <circle cx="9" cy="9" r="7" stroke="#bbb" strokeWidth="2" />
            <line x1="14" y1="14" x2="19" y2="19" stroke="#bbb" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            placeholder="Search merchants, transactions..."
            className="w-full h-[34px] bg-[#F3F4F5] rounded-[12px] pl-9 pr-3 text-[14px] text-[#6B7280] outline-none focus:border-violet-400 transition"
          />
        </div>
      </div>

      {/* RIGHT SECTION */}
      <div className="flex items-center gap-6 ml-6">

        {/* Credit / Debit */}
        <div className="flex items-center gap-8 text-xs">
          <div className="flex flex-col items-center leading-tight">
            <span className="text-slate-400 font-semibold">CREDIT</span>
            <span className="text-[#34094D] font-semibold">₹0.00</span>
          </div>
          <div className="h-6 w-px bg-gray-200" />
          <div className="flex flex-col items-center leading-tight">
            <span className="text-slate-400 font-semibold">DEBIT</span>
            <span className="text-red-600 font-semibold">₹0.00</span>
          </div>
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-gray-200" />

        {/* Bell */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#888"
          strokeWidth="2"
          className="cursor-pointer hover:stroke-violet-600 transition"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {/* Settings icon */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#888"
          strokeWidth="2"
          className="cursor-pointer hover:stroke-violet-600 transition"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>

        {/* Avatar + Name + Dropdown */}
        <div ref={menuRef} className="relative flex items-center gap-2">
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#2d1a5e", lineHeight: 1.2 }}>{userName || "Merchant"}</div>
            <div style={{ fontSize: 10, color: "#aaa", lineHeight: 1.2 }}>{userRole}</div>
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="relative"
          >
            {profilePhoto ? (
              <img src={profilePhoto} alt="avatar" className="w-8 h-8 rounded-[12px] object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-[12px] bg-gradient-to-br from-gray-900 to-indigo-800 flex items-center justify-center text-white text-[11px] font-bold">
                {userName?.substring(0, 2).toUpperCase() || "MP"}
              </div>
            )}
          </button>

          {/* Dropdown */}
          {menuOpen && (
            <div
              className="absolute right-0 top-[calc(100%+10px)] w-64 rounded-2xl z-50 overflow-hidden shadow-2xl"
              style={{ background: "#1e1e2e" }}
            >
              {/* Customer ID & Membership */}
              <div className="px-4 pt-4 pb-3 space-y-3 border-b border-white/10">
                <div className="flex items-center gap-3">
                  {/* Gear icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                  <span className="text-xs text-gray-400">
                    Customer ID: <span className="font-bold text-white">{userId || "—"}</span>
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  {/* Card icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                  <span className="text-xs text-gray-400">
                    Membership Type: <span className="font-bold text-white">API Partner</span>
                  </span>
                </div>
              </div>

              {/* My Profile */}
              <button
                onClick={() => { setMenuOpen(false); navigate("/profile"); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-white/10 transition border-b border-white/10"
              >
                {/* Person icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span className="font-medium">My Profile</span>
              </button>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-white/10 transition"
              >
                {/* Logout icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span className="font-medium">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
